/**
 * Registro de tipos de notificação — deve espelhar EXATAMENTE o que o backend
 * emite. Tipo emitido sem registro aqui cai no FALLBACK; tipo registrado sem
 * emissor é código morto que dá falsa sensação de cobertura.
 *
 * Emissores no backend (mantenha esta lista sincronizada ao mexer lá):
 *   anamnese.answer_received        Listeners/CreateOrAggregateAnamneseNotification
 *   assistant_note.received         Http/Controllers/AssistantNoteController
 *   assistant_note.reply            Http/Controllers/AssistantNoteController
 *   appointment.canceled_by_patient Services/AppointmentCancellationNotifier
 *   appointment_declined            Http/Controllers/WebhookController
 *   appointment_rescheduled         Http/Controllers/WebhookController
 *   appointment.starting            Console/Commands/NotifyAppointmentsStarting
 *   chat_message                    Http/Controllers/WebhookController
 *   insight.clinical                Services/InsightService
 *
 * ---------------------------------------------------------------------------
 * DESTINO (salvia-850)
 * ---------------------------------------------------------------------------
 * Cada tipo declara para ONDE o clique leva. Antes isto era `route()`, que só
 * sabia devolver rota do vue-router — e recado não é uma página, é um diálogo
 * global. O NotificationBadge então tinha um desvio hardcoded por prefixo de
 * tipo, que era uma SEGUNDA porta para a mesma ação: o registry dizia uma
 * coisa e o componente fazia outra.
 *
 * Agora há uma porta só. `destino(data)` devolve uma destas três formas, e a
 * camada de navegação (NotificationBadge) despacha sem conhecer tipo algum:
 *
 *   null                                        não navega
 *   { tipo: "rota",   rota }                    router.push(rota)
 *   { tipo: "evento", nome, payload }           $root.$emit(nome, payload)
 *
 * Tipo novo que precise abrir um diálogo global se resolve aqui, declarando o
 * evento — sem tocar no NotificationBadge.
 */

/** Atalhos para montar destino, só para o registro abaixo ficar legível. */
const paraRota = (rota) => ({ tipo: "rota", rota });
const paraEvento = (nome, payload = {}) => ({ tipo: "evento", nome, payload });

/**
 * Abre o prontuário do paciente; `query` extra quando o destino é uma aba.
 *
 * O `id` vai como STRING, e isto não é detalhe de estilo — é o que faz o
 * deep-link funcionar (salvia-850).
 *
 * O App.vue monta a tela com `<router-view :key="$route.params.id">`. Navegando
 * por nome com `params: { id: 11 }`, o vue-router guarda o número como veio, e
 * a chave passa a ser o número `11`. Qualquer navegação posterior que resolva a
 * rota pelo CAMINHO — inclusive o `replace` que a própria tela faz para limpar
 * o `?openChat=1` da URL — devolve `params.id` como a string `"11"`.
 *
 * O Vue compara chaves com `===`: `11 !== "11"`. A tela inteira era destruída e
 * recriada, perdendo o estado que o deep-link tinha acabado de montar. O
 * sintoma era o chat abrir e fechar no mesmo instante, parecendo que nunca
 * abriu.
 *
 * Com a string dos dois lados a chave não muda e nada é recriado.
 */
function rotaDoPaciente(data, query) {
  const id = data?.patient_id;

  return paraRota({
    name: "PatientProfileDetails",
    params: { id: id == null ? id : String(id) },
    ...(query ? { query } : {}),
  });
}

/**
 * Rota que abre uma consulta específica na agenda (salvia-789).
 *
 * Sem `event_id` cai na agenda genérica — é o comportamento antigo e continua
 * valendo para notificações gravadas antes desta mudança, que não têm o campo.
 */
function rotaDaConsulta(data) {
  const eventId = data?.event_id;
  if (!eventId) return paraRota({ name: "CalendarPage" });

  const query = { event_id: String(eventId) };
  if (data?.event_start) query.event_start = data.event_start;

  return paraRota({ name: "CalendarPage", query });
}

/**
 * Recado (médico <-> assistente): abre a JANELA DA CONVERSA, não a lista.
 *
 * salvia-850: o backend já mandava `note_id` desde sempre nas duas emissões
 * (`received` e `reply`) — o front é que jogava fora e abria a caixa de
 * entrada, deixando o médico procurar qual recado era.
 *
 * Sem `note_id` (notificações antigas) o widget cai na lista, que é o
 * comportamento anterior: nenhuma notificação já gravada piora.
 */
function destinoDoRecado(data) {
  return paraEvento("open-recados", { noteId: data?.note_id ?? null });
}

const REGISTRY = {
  "anamnese.answer_received": {
    icon: "mdi-message-text-outline",
    color: "#3E8E73",
    destino: (data) => rotaDoPaciente(data),
  },
  // Recados médico <-> assistente: widget global, aberto por evento.
  "assistant_note.received": {
    icon: "mdi-message-badge-outline",
    color: "#1F4D3F",
    destino: (data) => destinoDoRecado(data),
  },
  "assistant_note.reply": {
    icon: "mdi-reply-outline",
    color: "#3E8E73",
    destino: (data) => destinoDoRecado(data),
  },
  // Paciente cancelou a consulta → abre o cadastro do paciente (o "link" pedido).
  "appointment.canceled_by_patient": {
    icon: "mdi-calendar-remove",
    color: "#E53935",
    destino: (data) => rotaDoPaciente(data),
  },
  // Paciente respondeu ao webhook de confirmação dizendo que não comparecerá.
  // salvia-789: abre A CONSULTA em questão, não a agenda genérica. Antes o médico
  // recebia o aviso e caía numa agenda cheia, tendo que procurar qual era.
  // `event_start` leva a agenda para a semana certa antes de abrir o evento.
  "appointment_declined": {
    icon: "mdi-calendar-alert",
    color: "#E53935",
    destino: (data) => rotaDaConsulta(data),
  },
  // Paciente remanejou a consulta pelo webhook de confirmação.
  "appointment_rescheduled": {
    icon: "mdi-calendar-sync",
    color: "#FB8C00",
    destino: (data) => rotaDaConsulta(data),
  },
  // Paciente respondeu no WhatsApp → prontuário JÁ COM a aba Chat aberta.
  // O `openChat` existia aqui desde antes, mas nenhuma tela o lia: o clique
  // levava ao prontuário e o chat não abria. Quem consome agora é o
  // PatientProfileDashboardPage (salvia-850).
  "chat_message": {
    icon: "mdi-whatsapp",
    color: "#25D366",
    destino: (data) => rotaDoPaciente(data, { openChat: "1" }),
  },
  // salvia-779: a consulta começa em ~5 min. Ao vivo também abre o popup
  // (store `atendimento`); pelo sino, leva direto ao atendimento do paciente.
  "appointment.starting": {
    icon: "mdi-calendar-clock",
    color: "#B45309",
    // `event`: o atendimento já nasce ligado a esta consulta (histórico do kanban).
    destino: (data) =>
      rotaDoPaciente(data, {
        atendimento: "novo",
        ...(data?.event_id ? { event: String(data.event_id) } : {}),
      }),
  },
  // Insight clínico gerado por gatilho (Salvia Agêntico — Fase 1).
  // Abre o prontuário do paciente, onde vive o painel de insights.
  "insight.clinical": {
    icon: "mdi-lightbulb-on-outline",
    color: "#F9A825",
    destino: (data) => rotaDoPaciente(data),
  },
};

/**
 * Tipo desconhecido: mostra a notificação com ícone genérico e NÃO navega.
 *
 * Antes mandava para a LandingPage — a home pública, em `/` —, ou seja, o
 * clique chutava o usuário logado para fora da aplicação. Devolver `null` é o
 * contrato de "sem destino": o NotificationBadge só age quando há destino. O
 * texto da notificação continua visível e ela é marcada como lida normalmente.
 */
const FALLBACK = {
  icon: "mdi-bell-outline",
  color: "#757575",
  destino: () => null,
};

export function getNotificationMeta(type) {
  return REGISTRY[type] || FALLBACK;
}

export function getNotificationDestino(notification) {
  const meta = getNotificationMeta(notification?.type);
  return meta.destino(notification?.data || {});
}
